import React, { useEffect, useState } from "react";
import { Link2, Plus, X, Save, Loader2, Copy, ExternalLink, GripVertical } from "lucide-react";
import { getBioAdmin, saveBio, fetchProperties } from "../../api";
import { useToast } from "../../hooks/use-toast";

const QUALIFIERS = ["", "Guide Price", "Offers in Excess Of", "Offers Over", "Offers in Region of", "POA", "New Instruction", "Under Offer"];
const input = "w-full h-[44px] px-3 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]";
const label = "text-[12px] uppercase tracking-wide text-[#a0a0a0] mb-1.5 block";

const BioManager = () => {
  const { toast } = useToast();
  const [cfg, setCfg] = useState(null);
  const [props, setProps] = useState([]);
  const [saving, setSaving] = useState(false);
  const publicUrl = `${window.location.origin}/bio`;

  useEffect(() => {
    getBioAdmin().then(setCfg).catch(() => {});
    fetchProperties({ sort: "newest" }).then((d) => { const s = new Set(); setProps((d || []).filter((p) => (s.has(p.id) ? false : s.add(p.id)))); }).catch(() => {});
  }, []);

  if (!cfg) return <div className="flex items-center justify-center py-20 text-[#8a8a8a]"><Loader2 className="animate-spin mr-2" size={18} /> Loading…</div>;

  const set = (patch) => setCfg((c) => ({ ...c, ...patch }));
  const setBtn = (i, patch) => set({ buttons: cfg.buttons.map((b, j) => j === i ? { ...b, ...patch } : b) });
  const addLink = () => set({ customLinks: [...(cfg.customLinks || []), { title: "", url: "" }] });
  const setLink = (i, patch) => set({ customLinks: cfg.customLinks.map((l, j) => j === i ? { ...l, ...patch } : l) });
  const rmLink = (i) => set({ customLinks: cfg.customLinks.filter((_, j) => j !== i) });
  const addFeatured = () => set({ featured: [...(cfg.featured || []), { propertyId: "", qualifier: "" }] });
  const setFeatured = (i, patch) => set({ featured: cfg.featured.map((f, j) => j === i ? { ...f, ...patch } : f) });
  const rmFeatured = (i) => set({ featured: cfg.featured.filter((_, j) => j !== i) });

  const save = async () => {
    setSaving(true);
    try {
      const { featuredResolved, key, _id, ...payload } = cfg;
      await saveBio(payload);
      toast({ title: "Link in Bio saved", description: "Your public page is updated." });
    } catch { toast({ title: "Couldn't save" }); }
    finally { setSaving(false); }
  };

  const copyUrl = () => { navigator.clipboard.writeText(publicUrl); toast({ title: "Link copied", description: publicUrl }); };

  return (
    <div className="space-y-6" data-testid="bio-manager">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-[20px] font-500 text-[#2b2b2b] flex items-center gap-2"><Link2 size={18} className="text-[#b8935f]" /> Link in Bio</h3>
          <p className="text-[13px] text-[#8a8a8a] mt-1">One branded landing page for your Instagram bio — featured listings + action buttons.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={copyUrl} data-testid="bio-copy-url" className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white border border-[#e2d8c8] text-[13px] font-500 hover:border-[#b8935f] transition-colors"><Copy size={14} /> Copy link</button>
          <a href="/bio" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white border border-[#e2d8c8] text-[13px] font-500 hover:border-[#b8935f] transition-colors"><ExternalLink size={14} /> Preview</a>
          <button onClick={save} disabled={saving} data-testid="bio-save" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#b8935f] text-white text-[13px] font-500 hover:bg-[#a67f4d] transition-colors disabled:opacity-60">{saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-[#f0ece5] p-5 space-y-4">
          <h4 className="font-display text-[16px] font-500 text-[#2b2b2b]">Header</h4>
          <div><label className={label}>Headline</label><input value={cfg.headline || ""} onChange={(e) => set({ headline: e.target.value })} className={input} data-testid="bio-headline-input" /></div>
          <div><label className={label}>Tagline</label><input value={cfg.subheadline || ""} onChange={(e) => set({ subheadline: e.target.value })} className={input} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Contact email</label><input value={cfg.contactEmail || ""} onChange={(e) => set({ contactEmail: e.target.value })} className={input} /></div>
            <div><label className={label}>Contact phone</label><input value={cfg.contactPhone || ""} onChange={(e) => set({ contactPhone: e.target.value })} className={input} /></div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#f0ece5] p-5 space-y-4">
          <h4 className="font-display text-[16px] font-500 text-[#2b2b2b]">Action buttons</h4>
          {(cfg.buttons || []).map((b, i) => (
            <div key={b.key} className="flex items-center gap-2" data-testid={`bio-btn-row-${b.key}`}>
              <input type="checkbox" checked={b.enabled} onChange={(e) => setBtn(i, { enabled: e.target.checked })} className="w-4 h-4 accent-[#b8935f]" data-testid={`bio-btn-toggle-${b.key}`} />
              <input value={b.label} onChange={(e) => setBtn(i, { label: e.target.value })} className={input + " flex-1"} />
              <span className="text-[11px] text-[#a0a0a0] w-16 text-right capitalize">{b.key}</span>
            </div>
          ))}
          <div className="pt-2">
            <h4 className="font-display text-[16px] font-500 text-[#2b2b2b] mb-2">Social links</h4>
            <div className="space-y-2">
              {["instagram", "linkedin", "facebook"].map((s) => (
                <input key={s} value={(cfg.socials || {})[s] || ""} onChange={(e) => set({ socials: { ...(cfg.socials || {}), [s]: e.target.value } })} placeholder={`${s[0].toUpperCase() + s.slice(1)} URL`} className={input} />
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#f0ece5] p-5">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-display text-[16px] font-500 text-[#2b2b2b]">Featured listings</h4>
          <button onClick={addFeatured} data-testid="bio-add-featured" className="inline-flex items-center gap-1.5 text-[13px] text-[#b8935f] font-500"><Plus size={15} /> Add listing</button>
        </div>
        {(cfg.featured || []).length === 0
          ? <p className="text-[13px] text-[#8a8a8a]">No hand-picked listings — the page auto-shows your newest 6 for-sale homes. Add listings here to curate.</p>
          : (
            <div className="space-y-2">
              {cfg.featured.map((f, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`bio-featured-row-${i}`}>
                  <GripVertical size={16} className="text-[#d0c8b8] shrink-0" />
                  <select value={f.propertyId} onChange={(e) => setFeatured(i, { propertyId: e.target.value })} className={input + " flex-1"} data-testid={`bio-featured-property-${i}`}>
                    <option value="">Select property…</option>
                    {props.map((p) => <option key={p.id} value={p.id}>{p.title} — £{Number(p.price || 0).toLocaleString()}</option>)}
                  </select>
                  <select value={f.qualifier} onChange={(e) => setFeatured(i, { qualifier: e.target.value })} className={input + " w-48"}>
                    {QUALIFIERS.map((q) => <option key={q} value={q}>{q || "No qualifier"}</option>)}
                  </select>
                  <button onClick={() => rmFeatured(i)} className="text-[#c0a878] hover:text-red-500 shrink-0"><X size={18} /></button>
                </div>
              ))}
            </div>
          )}
      </div>

      <div className="bg-white rounded-2xl border border-[#f0ece5] p-5">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-display text-[16px] font-500 text-[#2b2b2b]">Custom links</h4>
          <button onClick={addLink} data-testid="bio-add-link" className="inline-flex items-center gap-1.5 text-[13px] text-[#b8935f] font-500"><Plus size={15} /> Add link</button>
        </div>
        {(cfg.customLinks || []).length === 0
          ? <p className="text-[13px] text-[#8a8a8a]">Add extra links — e.g. a market report, team page or Google reviews.</p>
          : (
            <div className="space-y-2">
              {cfg.customLinks.map((l, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input value={l.title} onChange={(e) => setLink(i, { title: e.target.value })} placeholder="Label" className={input + " w-48"} />
                  <input value={l.url} onChange={(e) => setLink(i, { url: e.target.value })} placeholder="https://…" className={input + " flex-1"} />
                  <button onClick={() => rmLink(i)} className="text-[#c0a878] hover:text-red-500 shrink-0"><X size={18} /></button>
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  );
};

export default BioManager;
